import { supplierListingsQuery } from "@/api/reporting";
import { ChartBox, formatAxisNumber, useContainerSize } from "@/components/charts/ChartBox";
import { Card } from "@/components/ui/Card";
import { Box, Checkbox, Field, Label, QueryHandler } from "@givenwell/components";
import { ListingSummary } from "@givenwell/management-api";
import { defineChart, rect, ruleY } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";
import { binX } from "@tanstack/charts/transform/bin";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

export const Route = createFileRoute("/dashboard/listing-prices")({
  component: PriceRangeCard,
});

function getThresholds() {
  const thresholds = [0, 1];
  for (let i = 10.01; i <= 5000.01; i += 10) {
    thresholds.push(i);
  }
  return thresholds;
}
const thresholds = getThresholds();

const statusOrder = ["Published", "Pending Approval", "Draft", "Declined"];
// Observable Plot's default categorical scheme (observable10), first four
// colors, mapped in the stack order the old chart used.
const statusColors = ["#4269d0", "#efb118", "#ff725c", "#6cc5b0"];

function PriceRangeCard() {
  const listingsQuery = useQuery(supplierListingsQuery());

  const [cutoff, setCutoff] = useState(true);

  return (
    <Box css={{ height: "100%" }}>
      <Card css={{ height: "100%", display: "flex", flexDirection: "column" }} title="Listing Prices">
        <QueryHandler
          query={listingsQuery}
          success={({ data: rawData }) => {
            const filteredData = rawData.filter((listing, index, all) => {
              if (all.find(l => l.listing_id === listing.listing_id && l.version > listing.version)) {
                return false;
              }
              if (listing.status === "Closed") {
                return false;
              }
              return true;
            });

            const pricedData = cutoff ? filteredData.filter(listing => listing.min_price < 500.01) : filteredData;

            return (
              <>
                <Field css={{ flexDirection: "row", mb: 16 }}>
                  <Checkbox checked={cutoff} onCheckedChange={setCutoff} />
                  <Label>Only show listings {"<$500"}</Label>
                </Field>

                <PriceHistogram data={pricedData} />
              </>
            );
          }}
        />
      </Card>
    </Box>
  );
}

type PriceBinRow = {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  status: string;
  count: number;
};

function PriceHistogram({ data }: { data: ListingSummary[] }) {
  const { ref, height } = useContainerSize();

  const definition = useMemo(() => {
    // Plot binned over the data extent, ignoring thresholds outside it (and
    // catching out-of-range values in the edge bins). The eager binX fixes
    // its domain to the first/last boundary, so clip the boundaries to the
    // extent ourselves to bin (and infer the x domain) identically.
    let lo = Infinity;
    let hi = -Infinity;
    for (const listing of data) {
      if (listing.min_price < lo) lo = listing.min_price;
      if (listing.min_price > hi) hi = listing.min_price;
    }
    const boundaries = data.length ? [lo, ...thresholds.filter(t => t > lo && t < hi), hi] : thresholds;

    const bins = binX(data, {
      value: "min_price",
      by: "status",
      // A single distinct price cannot form a bin boundary pair; widen it.
      thresholds: new Set(boundaries).size >= 2 ? boundaries : [lo, lo + 1],
    });

    // Stack the per-status counts within each bin, bottom-up in statusOrder.
    const binsByStart = new Map<number, typeof bins>();
    for (const bin of bins) {
      const group = binsByStart.get(bin.x1);
      if (group) {
        group.push(bin);
      } else {
        binsByStart.set(bin.x1, [bin]);
      }
    }

    const rows: PriceBinRow[] = [];
    for (const group of binsByStart.values()) {
      group.sort((a, b) => statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status));
      let y = 0;
      for (const bin of group) {
        rows.push({ x1: bin.x1, x2: bin.x2, y1: y, y2: y + bin.value, status: bin.status, count: bin.value });
        y += bin.value;
      }
    }

    return defineChart({
      marks: [
        rect(rows, {
          x1: "x1",
          x2: "x2",
          y1: "y1",
          y2: "y2",
          color: "status",
          inset: 0,
        }),
        ruleY([0]),
      ],
      scales: {
        x: {
          scale: scaleLinear,
          axis: { label: "Starting price for listing", ticks: { format: formatAxisNumber } },
        },
        y: {
          scale: scaleLinear,
          grid: false,
          axis: { ticks: { format: formatAxisNumber } },
        },
      },
      color: {
        domain: statusOrder,
        range: statusColors,
      },
      tooltip: {
        use: tooltip,
        format: point =>
          `$${point.datum.x1}–$${point.datum.x2}\n${point.datum.count} listing${point.datum.count === 1 ? "" : "s"}\n${point.datum.status}`,
      },
    });
  }, [data]);

  return (
    <ChartBox ref={ref} css={{ height: "100%", flex: "1 0 0px" }}>
      {height !== undefined && <Chart definition={definition} height={height} ariaLabel="Listing prices histogram" />}
    </ChartBox>
  );
}
